import { useState, useEffect, useRef, useCallback } from 'react';

export function useWaitingTimer(isInsideGeofence, hasArrived, freeMinutes = 5, feePerMin = 1.1, maxMinutes = 45) {
  const [totalSeconds, setTotalSeconds] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [segments, setSegments] = useState([]);
  const intervalRef = useRef(null);
  const segmentStartRef = useRef(null);

  // Manage interval lifecycle: depend ONLY on driving inputs (hasArrived, isInsideGeofence),
  // NOT on internal state (isRunning, isPaused) — that caused a cleanup-on-restart loop
  // where the interval was killed by the cleanup phase before it could tick.
  useEffect(() => {
    // Pause/stop path: not arrived, OR arrived but outside geofence
    if (!hasArrived || !isInsideGeofence) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      // If a segment was open, close it
      if (segmentStartRef.current) {
        const enteredAt = segmentStartRef.current;
        const leftAt = Date.now();
        setSegments((prev) => [
          ...prev,
          { enteredAt, leftAt, durationSeconds: Math.round((leftAt - enteredAt) / 1000) },
        ]);
        segmentStartRef.current = null;
        setIsPaused(true);
      }
      return;
    }

    // Run path: hasArrived && isInsideGeofence
    // Guard: if interval already running, do nothing (prevents duplicate intervals)
    if (intervalRef.current) return;

    setIsRunning(true);
    setIsPaused(false);
    segmentStartRef.current = Date.now();

    intervalRef.current = setInterval(() => {
      setTotalSeconds((prev) => {
        if (prev >= maxMinutes * 60) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          return prev;
        }
        return prev + 1;
      });
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [hasArrived, isInsideGeofence, maxMinutes]);

  const totalMinutes = totalSeconds / 60;
  const freeSecondsRemaining = Math.max(0, freeMinutes * 60 - totalSeconds);
  const chargeableSeconds = Math.max(0, totalSeconds - freeMinutes * 60);
  const chargeableMinutes = chargeableSeconds / 60;
  const waitingFee = Math.round(chargeableMinutes * feePerMin * 100) / 100;
  const isInFreeWindow = totalSeconds < freeMinutes * 60;
  const maxReached = totalSeconds >= maxMinutes * 60;

  const getTimerData = useCallback(() => ({
    totalSeconds,
    segments,
    freeMinutes,
    feePerMin,
    maxMinutes,
    waitingFee,
    isRunning,
    isPaused,
  }), [totalSeconds, segments, freeMinutes, feePerMin, maxMinutes, waitingFee, isRunning, isPaused]);

  const formatTime = useCallback((secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }, []);

  return {
    totalSeconds,
    totalMinutes,
    freeSecondsRemaining,
    waitingFee,
    isRunning,
    isPaused,
    isInFreeWindow,
    maxReached,
    segments,
    getTimerData,
    formatTime: () => formatTime(totalSeconds),
    formatFreeRemaining: () => formatTime(Math.round(freeSecondsRemaining)),
  };
}